import { createContext, useCallback, useContext, useEffect, useMemo, useState, type FormEvent, type ReactElement, type ReactNode } from "react";
import { PRODUCT_NAME } from "@kuroo/brand";
import type { AuthUser, PublicBranding } from "@kuroo/contracts";
import { KurooApiError, type LoginResult, type MfaLoginChallenge, type OrganizationListItem } from "@kuroo/sdk";
import { assertionPayload, browserRequestOptions } from "./webauthn";
import { dataError, isUnauthenticated } from "./backoffice-utils";
import { brandingAsset, client } from "./backoffice-runtime";

type AuthContextValue = {
  user?: AuthUser;
  loading: boolean;
  error?: string;
  organizations: OrganizationListItem[];
  organization?: OrganizationListItem;
  organizationLoading: boolean;
  organizationError?: string;
  pendingMfa?: MfaLoginChallenge;
  signIn: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (code: string, assertion?: Record<string, unknown>) => Promise<void>;
  verifyMfaWithPasskey: () => Promise<void>;
  recoverMfa: (code: string) => Promise<void>;
  cancelMfa: () => void;
  completePasswordChange: () => void;
  signOut: () => Promise<void>;
  selectOrganization: (organizationId: string) => Promise<void>;
  expire: () => void;
};
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is required");
  return value;
}

function adminRecoveryError(error: unknown): string {
  // The recovery endpoint deliberately masks a missing/mismatched secret as
  // NOT_FOUND. The backoffice can be more actionable because the operator is
  // already looking at the dedicated recovery form.
  if (error instanceof KurooApiError && error.code === "NOT_FOUND") {
    return "El token temporal no coincide o la variable ADMIN_RECOVERY_TOKEN no está configurada en el Worker API. Ejecutá npm run production:admin-recover, copiá el token mostrado una sola vez y volvé a intentarlo.";
  }
  return dataError(error);
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<AuthUser>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [organizations, setOrganizations] = useState<OrganizationListItem[]>([]);
  const [organization, setOrganization] = useState<OrganizationListItem>();
  const [organizationLoading, setOrganizationLoading] = useState(false);
  const [organizationError, setOrganizationError] = useState<string>();
  const [pendingMfa, setPendingMfa] = useState<MfaLoginChallenge>();
  useEffect(() => {
    void client.currentUser().then(setUser).catch((reason: unknown) => {
      if (!isUnauthenticated(reason)) setError(reason instanceof Error ? reason.message : "No se pudo validar la sesión.");
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!user) {
      client.setOrganizationId(undefined);
      setOrganizations([]);
      setOrganization(undefined);
      setOrganizationError(undefined);
      setOrganizationLoading(false);
      return;
    }
    let active = true;
    setOrganizationLoading(true);
    setOrganizationError(undefined);
    void Promise.all([client.organizations(), client.organizationContext()])
      .then(([items, resolved]) => {
        if (!active) return;
        client.setOrganizationId(resolved.context.organizationId);
        setOrganizations(items);
        setOrganization(items.find((item) => item.id === resolved.context.organizationId) ?? { ...resolved.organization, membership: resolved.membership });
        // The installation-global user role is not authoritative for tenant
        // screens. Use the verified membership role for navigation and UI
        // affordances while the API enforces the same rule server-side.
        setUser((current) => current && current.id === user.id ? { ...current, role: resolved.membership.role } : current);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        client.setOrganizationId(undefined);
        setOrganizationError(dataError(reason));
      })
      .finally(() => { if (active) setOrganizationLoading(false); });
    return () => { active = false; };
  }, [user?.id]);
  const expire = useCallback(() => { client.setOrganizationId(undefined); setPendingMfa(undefined); setUser(undefined); }, []);
  const value = useMemo<AuthContextValue>(() => ({
    user, loading, error, organizations, organization, organizationLoading, organizationError, pendingMfa,
    signIn: async (email, password) => {
      setError(undefined);
      client.setOrganizationId(undefined);
      const next = await client.login(email, password);
      if ("challengeId" in next && next.mfaRequired) {
        setPendingMfa(next);
        setUser(undefined);
      } else {
        setPendingMfa(undefined);
        setUser(next);
      }
      return next;
    },
    verifyMfa: async (code, assertion) => {
      if (!pendingMfa) throw new Error("No hay un desafío MFA pendiente.");
      const next = await client.verifyMfa({ challengeId: pendingMfa.challengeId, challenge: pendingMfa.challenge, code, assertion });
      setPendingMfa(undefined);
      setUser(next);
    },
    verifyMfaWithPasskey: async () => {
      if (!pendingMfa?.publicKey || !pendingMfa.methods.includes("webauthn")) throw new Error("Esta cuenta no tiene un passkey disponible.");
      if (!window.PublicKeyCredential || !navigator.credentials) throw new Error("Este navegador no admite passkeys.");
      const credential = await navigator.credentials.get({ publicKey: browserRequestOptions(pendingMfa.publicKey) });
      if (!(credential instanceof PublicKeyCredential)) throw new Error("No se obtuvo una credencial passkey.");
      const next = await client.verifyMfa({ challengeId: pendingMfa.challengeId, challenge: pendingMfa.challenge, assertion: assertionPayload(credential) });
      setPendingMfa(undefined);
      setUser(next);
    },
    recoverMfa: async (code) => {
      if (!pendingMfa) throw new Error("No hay un desafío MFA pendiente.");
      const next = await client.recoverMfa({ challengeId: pendingMfa.challengeId, code });
      setPendingMfa(undefined);
      setUser(next);
    },
    cancelMfa: () => setPendingMfa(undefined),
    completePasswordChange: () => setUser((current) => current ? { ...current, mustChangePassword: false } : current),
    signOut: async () => { await client.logout().catch(() => undefined); client.setOrganizationId(undefined); setPendingMfa(undefined); setUser(undefined); },
    selectOrganization: async (organizationId) => {
      const resolved = await client.switchOrganization(organizationId);
      setOrganization({ ...resolved.organization, membership: resolved.membership });
      setUser((current) => current ? { ...current, role: resolved.membership.role } : current);
      setOrganizationError(undefined);
    },
    expire
  }), [user, loading, error, organizations, organization, organizationLoading, organizationError, pendingMfa, expire]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function LoginPage({ navigate, branding }: { navigate: (path: string) => void; branding: PublicBranding }): ReactElement {
  const { user, pendingMfa, signIn, verifyMfa, verifyMfaWithPasskey, recoverMfa, cancelMfa, error: authError } = useAuth();
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [code, setCode] = useState(""); const [mfaRecoveryMode, setMfaRecoveryMode] = useState(false); const [adminRecoveryMode, setAdminRecoveryMode] = useState(false); const [recoveryEmail, setRecoveryEmail] = useState(""); const [recoveryNewEmail, setRecoveryNewEmail] = useState(""); const [recoveryToken, setRecoveryToken] = useState(""); const [recoveryPassword, setRecoveryPassword] = useState(""); const [recoveryConfirmation, setRecoveryConfirmation] = useState(""); const [error, setError] = useState<string>(); const [notice, setNotice] = useState<string>(); const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);
  const submit = async (event: FormEvent) => { event.preventDefault(); setBusy(true); setError(undefined); setNotice(undefined); try { const result = await signIn(email, password); if (!("challengeId" in result)) navigate("/dashboard"); } catch (reason) { setError(dataError(reason)); } finally { setBusy(false); } };
  const submitMfa = async (event: FormEvent) => { event.preventDefault(); if (!code.trim()) return; setBusy(true); setError(undefined); try { if (mfaRecoveryMode) await recoverMfa(code.trim()); else await verifyMfa(code.trim()); navigate("/dashboard"); } catch (reason) { setError(dataError(reason)); } finally { setBusy(false); } };
  const usePasskey = async () => { setBusy(true); setError(undefined); try { await verifyMfaWithPasskey(); navigate("/dashboard"); } catch (reason) { setError(dataError(reason)); } finally { setBusy(false); } };
  const submitAdminRecovery = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(undefined); setNotice(undefined);
    try {
      const result = await client.recoverAdminPassword({ email: recoveryEmail, newPassword: recoveryPassword, confirmPassword: recoveryConfirmation, ...(recoveryNewEmail.trim() ? { newEmail: recoveryNewEmail.trim() } : {}) }, recoveryToken.trim());
      setEmail(result.email); setPassword(""); setAdminRecoveryMode(false); setRecoveryToken(""); setRecoveryPassword(""); setRecoveryConfirmation(""); setRecoveryNewEmail("");
      setNotice("Acceso recuperado. Ingresá con la nueva contraseña.");
    } catch (reason) { setError(adminRecoveryError(reason)); }
    finally { setBusy(false); }
  };
  const showAdminRecovery = () => { setRecoveryEmail(email); setAdminRecoveryMode(true); setError(undefined); setNotice(undefined); };
  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand-mark"><span>{brandingAsset(branding.logoUrl) ? <img src={brandingAsset(branding.logoUrl)} alt="" /> : "K"}</span><div><strong>{branding.businessName || PRODUCT_NAME}</strong><small>{branding.productName} · Operación de reservas</small></div></div>
        {pendingMfa ? <>
          <h1>Verificación adicional</h1>
          <p className="lede">Completá el segundo factor para proteger tu cuenta.</p>
          {(error || authError) && <div className="alert" role="alert">{error || authError}</div>}
          {pendingMfa.publicKey && pendingMfa.methods.includes("webauthn") && <button className="button secondary" type="button" onClick={() => void usePasskey()} disabled={busy}>{busy ? "Esperando passkey…" : "Continuar con passkey"}</button>}
          <form className="stack-form" onSubmit={(event) => void submitMfa(event)}>
            <label>{mfaRecoveryMode ? "Código de recuperación" : "Código de autenticación"}<input inputMode={mfaRecoveryMode ? "text" : "numeric"} autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} required /></label>
            <button className="button primary" disabled={busy}>{busy ? "Verificando…" : "Verificar"}</button>
          </form>
          <div className="button-row"><button className="text-button" type="button" onClick={() => { setMfaRecoveryMode((value) => !value); setCode(""); }}>{mfaRecoveryMode ? "Usar autenticador" : "Usar código de recuperación"}</button><button className="text-button" type="button" onClick={() => { cancelMfa(); setError(undefined); }}>Volver</button></div>
          <small className="muted">Métodos configurados: {pendingMfa.methods.join(", ")}</small>
        </> : adminRecoveryMode ? <>
          <h1>Recuperar acceso</h1>
          <p className="lede">Usá el token temporal de administración de producción. Se consume una sola vez.</p>
          <p className="field-help">El token se genera fuera del navegador con <code>npm run production:admin-recover</code> y se muestra una sola vez.</p>
          {error && <div className="alert" role="alert">{error}</div>}
          <form className="stack-form" onSubmit={(event) => void submitAdminRecovery(event)}>
            <label>Email actual<input type="email" autoComplete="email" value={recoveryEmail} onChange={(event) => setRecoveryEmail(event.target.value)} required /></label>
            <label>Nuevo email (opcional)<input type="email" autoComplete="email" value={recoveryNewEmail} onChange={(event) => setRecoveryNewEmail(event.target.value)} placeholder="Conservar el email actual" /></label>
            <label>Token temporal<input type="password" autoComplete="off" value={recoveryToken} onChange={(event) => setRecoveryToken(event.target.value)} required /></label>
            <label>Nueva contraseña<input type="password" autoComplete="new-password" minLength={12} value={recoveryPassword} onChange={(event) => setRecoveryPassword(event.target.value)} required /></label>
            <label>Confirmar contraseña<input type="password" autoComplete="new-password" minLength={12} value={recoveryConfirmation} onChange={(event) => setRecoveryConfirmation(event.target.value)} required /></label>
            <button className="button primary" disabled={busy}>{busy ? "Recuperando…" : "Restablecer acceso"}</button>
          </form>
          <button className="text-button" type="button" onClick={() => { setAdminRecoveryMode(false); setError(undefined); }}>Volver al inicio de sesión</button>
        </> : <>
          <h1>Ingresá a tu negocio</h1>
          <p className="lede">Gestioná agenda, clientes y cobros desde un solo lugar.</p>
          {notice && <div className="toast" role="status">{notice}</div>}
          {(error || authError) && <div className="alert" role="alert">{error || authError}</div>}
          <form className="stack-form" onSubmit={(event) => void submit(event)}>
            <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
            <label>Contraseña<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
            <button className="button primary" disabled={busy}>{busy ? "Ingresando…" : "Ingresar"}</button>
          </form>
          <button className="text-button" type="button" onClick={showAdminRecovery}>¿No podés ingresar? Recuperar acceso</button>
        </>}
      </section>
    </main>
  );
}

export function ChangePasswordPage({ navigate }: { navigate: (path: string) => void }): ReactElement {
  const { user, completePasswordChange, signOut } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setError(undefined);
    try {
      try {
        await client.changePassword({ currentPassword, newPassword, confirmPassword });
      } catch (reason) {
        // The initial login returns the user and establishes an HttpOnly
        // session cookie in a separate response. If the browser did not keep
        // that cookie (for example after a cross-origin redirect), re-create
        // the same password session with the temporary credential and retry
        // the change. The normal authenticated endpoint remains strict.
        if (!(reason instanceof KurooApiError) || reason.code !== "UNAUTHENTICATED" || !user?.email) throw reason;
        const refreshed = await client.login(user.email, currentPassword);
        if ("challengeId" in refreshed && refreshed.mfaRequired) throw new KurooApiError("MFA_REQUIRED", "Completá el segundo factor antes de cambiar la contraseña.");
        await client.changePassword({ currentPassword, newPassword, confirmPassword });
      }
      completePasswordChange(); navigate("/dashboard");
    }
    catch (reason) { setError(dataError(reason)); }
    finally { setBusy(false); }
  };
  return <main className="login-shell"><section className="login-card password-card"><div className="brand-mark"><span>K</span><div><strong>{PRODUCT_NAME}</strong><small>Seguridad de la cuenta</small></div></div><span className="tag warning">Acción requerida</span><h1>Actualizá tu contraseña</h1><p className="lede">La cuenta de {user?.email} fue creada con una contraseña temporal. Elegí una contraseña personal para continuar.</p>{error && <div className="alert" role="alert">{error}</div>}<form className="stack-form" onSubmit={(event) => void submit(event)}><label>Contraseña temporal<input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required /></label><label>Nueva contraseña<input type="password" autoComplete="new-password" minLength={12} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} required /><small className="field-help">Mínimo 12 caracteres. No reutilices la temporal.</small></label><label>Confirmar contraseña<input type="password" autoComplete="new-password" minLength={12} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required /></label><button className="button primary" disabled={busy}>{busy ? "Guardando…" : "Guardar y continuar"}</button></form><button className="text-button" type="button" onClick={() => void signOut()}>Cerrar sesión</button></section></main>;
}
